import React from 'react';
import type { NavPage } from '../utils/routes';
import Header from './Header';
import { AuthWall } from './AuthState';
import { T } from '../designTokens';
import { useCreditInfo } from '../hooks/useCreditInfo';
import { ScanProgress as ScanProgressType } from '../utils/api';

interface RouteLoadingRouteProps {
  scanRouteId: string | null;
  reportRouteId: string | null;
  isRouteLoading: boolean;
  hasCurrentResult: boolean;
  error: string | null;
  routeProgress: ScanProgressType | null;
  navigateTo: (page: NavPage) => void;
  queueCount: number;
  authEnabled: boolean;
  creditInfo: ReturnType<typeof useCreditInfo>;
  onBillingClick: () => void;
}

// The two #/scan/:id and #/report/:id "no result yet" interim states —
// extracted verbatim out of App()'s pair of `if (...) return ...` branches
// (issue #416). Both only ever render while a route id is present and no
// result has loaded for it yet, so — like StandalonePagesRoute and
// SecondaryPagesRoute — this is called directly as a plain function
// (`renderRouteLoadingRoute(props)`) rather than as JSX: App.tsx needs the
// actual `ReactElement | null` to decide whether to fall through to its own
// remaining branches. It uses no hooks, so calling it directly is safe.
export function renderRouteLoadingRoute({
  scanRouteId,
  reportRouteId,
  isRouteLoading,
  hasCurrentResult,
  error,
  routeProgress,
  navigateTo,
  queueCount,
  authEnabled,
  creditInfo,
  onBillingClick,
}: RouteLoadingRouteProps): React.ReactElement | null {
  if (!(scanRouteId || reportRouteId)) return null;

  if (isRouteLoading && !hasCurrentResult) {
    return (
      <div style={{ minHeight: '100dvh', backgroundColor: T.gray900 }}>
        <Header currentPage="scanner" onNavigate={navigateTo} queueCount={queueCount} authEnabled={authEnabled} creditBalance={creditInfo.balance} creditTier={creditInfo.tier} creditLoading={creditInfo.loading} creditHistory={creditInfo.hasCreditHistory} onBillingClick={onBillingClick} />
        <main className="report-container">
          <div
            style={{
              backgroundColor: T.gray800,
              borderRadius: '10px',
              padding: '48px',
              textAlign: 'center',
              border: `1px solid ${T.overlaySoft}`,
            }}
          >
            <h3 style={{ margin: '0 0 8px', color: T.gray100, fontFamily: T.fontBody, fontWeight: 700, fontSize: '18px' }}>
              {routeProgress ? 'Scanning…' : 'Loading scan results…'}
            </h3>
            {routeProgress && (
              <>
                <div style={{ maxWidth: '420px', margin: '16px auto 10px', height: '4px', backgroundColor: T.overlayLine, borderRadius: '2px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: '100%',
                      transform: `scaleX(${Math.min(100, Math.max(0, routeProgress.percentage)) / 100})`,
                      transformOrigin: 'left',
                      backgroundColor: T.cobalt500,
                      transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  />
                </div>
                <p style={{ margin: '0 0 12px', color: T.gray400, fontSize: '13px' }}>{routeProgress.message}</p>
              </>
            )}
            <p style={{ margin: 0, color: T.gray500, fontSize: '13px', fontFamily: T.fontMono }}>#{scanRouteId}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!isRouteLoading && !hasCurrentResult && !error) {
    return (
      <div style={{ minHeight: '100dvh', backgroundColor: T.gray50 }}>
        <Header currentPage="scanner" onNavigate={navigateTo} queueCount={queueCount} authEnabled={authEnabled} creditBalance={creditInfo.balance} creditTier={creditInfo.tier} creditLoading={creditInfo.loading} creditHistory={creditInfo.hasCreditHistory} onBillingClick={onBillingClick} />
        <main className="dashboard-container">
          <AuthWall
            title="Sign in to view this report"
            message="This report is private and scoped to the owner's account. Sign in to view it."
          />
        </main>
      </div>
    );
  }

  return null;
}
